/**
 * "Demandes à valider" — work-stoppage requests collabs made from Congés, which HR now
 * answers here instead of managers in the Congés list.
 *
 * Validating is not a bare status flip: it opens the declaration form on that leave, and
 * saving the suivi is what validates it, so no arrêt is ever validated without its suivi.
 * Refusing asks for a second click, since it notifies the collab.
 *
 * Collapsed by default — it sits above the table and must not push it off screen.
 */
import React from "react";
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Button, Chip, Stack, Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { confirmDialog } from "../popups/ConfirmDialog";
import { STATUS_PALETTE } from "./WorkStoppageStatus";

/**
 * @param {Object} props
 * @param {Array} props.requests - pending planning leaves (getEligibleLeaves only_pending)
 * @param {Function} props.onValidate - opens the declaration form on a request
 * @param {Function} props.onReject - refuses a request
 * @param {Object} [props.sx]
 */
export default function WorkStoppagePendingRequests({ requests, onValidate, onReject, sx }) {
  // "Refuser" asks first: a refused request cannot be taken back from here.
  const askReject = (leave) => {
    confirmDialog({
      title: <Langs str="REJECT_REQUEST_TITLE" />,
      message: <Langs str="REJECT_WORK_STOPPAGE_REQUEST_MSG" params={{ NAME: `${leave.prenom || ""} ${leave.nom || ""}`.trim() }} />,
      danger: true,
      icon: BlockRoundedIcon,
      confirmLabel: <Langs str="WORK_STOPPAGE_REJECT_CONFIRM" />,
      onConfirm: () => onReject(leave),
    });
  };

  if (!requests || !requests.length) return null;

  const count = requests.length;
  const title = count === 1
    ? Localize("WORK_STOPPAGE_PENDING_TITLE_ONE")
    : Localize("WORK_STOPPAGE_PENDING_TITLE_MANY").replace("%n%", count);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${STATUS_PALETTE.PENDING.fg}55`,
        borderRadius: "12px !important",
        bgcolor: STATUS_PALETTE.PENDING.bg,
        "&:before": { display: "none" },
        ...sx,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HourglassTopRoundedIcon fontSize="small" sx={{ color: STATUS_PALETTE.PENDING.fg }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: STATUS_PALETTE.PENDING.fg }}>
            {title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          <Langs str="WORK_STOPPAGE_PENDING_HELP" />
        </Typography>
        <Box sx={{ maxHeight: 260, overflowY: "auto", bgcolor: "background.paper", borderRadius: "10px" }}>
          {requests.map((leave, index) => (
            <Stack
              key={leave.id}
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ px: 1.5, py: 1, borderTop: index ? "1px solid" : "none", borderColor: "divider" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {`${leave.prenom || ""} ${leave.nom || ""}`}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {`${moment(Number(leave.from)).format("DD/MM/YYYY")} → ${moment(Number(leave.to)).format("DD/MM/YYYY")}`}
                  </Typography>
                  {leave.leave_label ? (
                    <Chip size="small" label={`${leave.type ? `${leave.type} - ` : ""}${leave.leave_label}`}
                      sx={{ height: 20, fontSize: "0.7rem", borderRadius: "6px" }} />
                  ) : null}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button size="small" color="error" onClick={() => askReject(leave)}>
                  <Langs str="WORK_STOPPAGE_REJECT" />
                </Button>
                <Button size="small" variant="contained" color="warning" onClick={() => onValidate(leave)}>
                  <Langs str="WORK_STOPPAGE_VALIDATE" />
                </Button>
              </Stack>
            </Stack>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
